import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { InvoiceCreationService } from '@app/sales/invoice-creation/invoice-creation.service';
import { QuotationService } from '@app/sales/quotation/quotation.service';
import { AlertService } from '@app/shared/alert.service';
import { FormArray, FormGroup, FormControl, FormBuilder, AbstractControl } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { Sales_order, Sales_invoice, Delivery, lineNetTaxx, roundHalfAway, vatTotals } from '@app/shared/_models/sales';

import { environment } from '@app/../environments/environment';
//import * as constants from "@app/shared/constants";
import { SalesOrderState } from '@app/shared/constants-server';
import { salesOrderLabels, getStateKey, getOrderLabel } from '@app/shared/constants';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { RouterLink } from '@angular/router';
import { InvoiceCreationActionComponent } from '../../shared/action-bar/invoice-creation-action/invoice-creation-action.component';
import { DatePipe, DecimalPipe } from '@angular/common';
import { ActionBarComponent } from '../../shared/action-bar/action-bar.component';
import { MenuCommonComponent } from '../../shared/menu-common/menu-common.component';
import { first, finalize } from 'rxjs';
import { ApiResponse } from '@app/shared/_models/common';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-invoice-creation',
  templateUrl: './invoice-creation.component.html',
  styleUrls: ['./invoice-creation.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MenuCommonComponent,
    ActionBarComponent,
    InvoiceCreationActionComponent,
    RouterLink,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatCheckboxModule,
    MatExpansionModule,
    DatePipe,
    DecimalPipe
],
})
export class InvoiceCreationComponent implements OnInit {
  constructor(
    private invoiceCreationService: InvoiceCreationService,
    private quotationService: QuotationService,
    private alertService: AlertService
  ) {}
  public SalesOrderState = SalesOrderState;
  public salesOrderLabels = salesOrderLabels;
  public datasource: Sales_invoice[] = [];
  public loading = true;
  public selectedDeliveryIds = new Set<number>();

  toggleSelection(deliveryId: number, checked: boolean) {
    if (checked) {
      this.selectedDeliveryIds.add(deliveryId);
    } else {
      this.selectedDeliveryIds.delete(deliveryId);
    }
  }

  selectAllDeliveries(): void {
    if (!this.datasource) return;
    for (const invoice of this.datasource) {
      if (invoice.lnk_deliveries) {
        for (const delivery of invoice.lnk_deliveries) {
          if (delivery.id !== undefined && delivery.id !== null) {
            this.selectedDeliveryIds.add(delivery.id);
          }
        }
      }
    }
  }

  deselectAllDeliveries(): void {
    this.selectedDeliveryIds.clear();
  }

  getTotalDeliveriesCount(): number {
    if (!this.datasource) return 0;
    let count = 0;
    for (const invoice of this.datasource) {
      if (invoice.lnk_deliveries) {
        count += invoice.lnk_deliveries.length;
      }
    }
    return count;
  }

  ngOnInit(): void {
    this.loadData();
  }

  loadData(): void {
    this.loading = true;
    this.invoiceCreationService.getDeliveriesToInvoice()
      .pipe(
        first(),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (data: Sales_invoice[]) => {
          this.datasource = data || [];
          this.selectedDeliveryIds.clear();
        },
        error: (e) => {
          const serverMsg = e.error?.message || e.error || e.message || 'Erreur inconnue';
          Swal.fire({
            title: 'Erreur',
            text: `Erreur au chargement des commandes à facturer.\nDétails: ${serverMsg}`,
            icon: 'error',
            confirmButtonText: 'Fermer'
          });
          console.error(e);
        }
      });
  }

  onClickChangeState(delivery_id: number, source_state: SalesOrderState, target_state: SalesOrderState) {
    console.log('onClickDeliver: ' + delivery_id);
    for (const invoice of this.datasource) {
      if (invoice.lnk_deliveries) {
        for (const delivery of invoice.lnk_deliveries) {
          if (delivery.id === delivery_id) {
            if (delivery.lnk_order) {
              delivery.lnk_order.state = target_state;
            }
            return;
          }
        }
      }
    }
  }

  getDeliveryLines(delivery: Delivery): any[] {
    if (!delivery || !delivery.lnk_deliveryl || !delivery.lnk_order || !delivery.lnk_order.lines) {
      return [];
    }
    return delivery.lnk_deliveryl.map((dl: any) => {
      const orderLine = delivery.lnk_order!.lines.find((ol: any) => ol.id === dl.id_order_l);
      const net = orderLine ? InvoiceCreationComponent.deliveredLineTaxx(orderLine, dl.qty) : 0;
      return {
        product_name: orderLine ? orderLine.product_name : 'Produit inconnu',
        qty: dl.qty,
        // Prix TTC du conditionnement APRÈS remise (unet absent = tarif, comme le serveur).
        uprice_taxi: orderLine ? +(orderLine.unet_taxi ?? orderLine.uprice_taxi) : 0,
        total_taxi: orderLine ? roundHalfAway(net * (1 + (+orderLine.vat_rate || 0) / 100), 2) : 0, // indicatif, éco-participation incluse dans le prix net
      };
    });
  }

  // BT-131 de la ligne pour la quantité livrée : round(qté × unet HT, 2), éco-participation déjà dans unet (getOrderSafePayload.sql, chemin BL).
  static deliveredLineTaxx(orderLine: any, qty: number): number {
    return lineNetTaxx(qty, +(orderLine.unet_taxx ?? orderLine.uprice_taxx));
  }

  // TTC que le serveur scellera pour ce BL : bases par taux, TVA arrondie UNE fois par taux (BG-23).
  getDeliveryTotal(delivery: Delivery): number {
    if (!delivery || !delivery.lnk_deliveryl || !delivery.lnk_order || !delivery.lnk_order.lines) {
      return 0;
    }
    const lines: { vat_rate: any; net_taxx: number }[] = [];
    for (const dl of delivery.lnk_deliveryl as any[]) {
      const orderLine = delivery.lnk_order!.lines.find((ol: any) => ol.id === dl.id_order_l);
      if (!orderLine || !(+dl.qty > 0)) continue; // le payload ne prend que les lignes livrées (dl.qty > 0)
      lines.push({ vat_rate: orderLine.vat_rate, net_taxx: InvoiceCreationComponent.deliveredLineTaxx(orderLine, +dl.qty) });
    }
    return vatTotals(lines).amount_taxi;
  }

  onActionBarAction(action: string) {
    console.log('InvoiceCreationComponent - onActionBarAction: ' + action);
    switch (action) {
      case 'INVOICING':
        console.log('[onActionBarAction] this.selectedDeliveryIds:', this.selectedDeliveryIds);
        this.quotationService.postInvoicingEvent(this.selectedDeliveryIds).pipe(first()).subscribe({
          next: (response: ApiResponse) => {
            console.log('Back from postInvoicingEvent', response);
            if (response && response.data && response.data.failed_invoices && response.data.failed_invoices.length > 0) {
              const errorMessages = response.data.failed_invoices.map((fi: any) => `<li>Facture <b>${fi.delivery_id}</b> : ${fi.error}</li>`).join('');
              Swal.fire({
                title: 'Attention',
                html: `L'opération a été traitée avec des erreurs :<br><ul style="text-align: left; max-height: 200px; overflow-y: auto;">${errorMessages}</ul>`,
                icon: 'warning',
                confirmButtonText: 'OK'
              });
            } else {
              this.alertService.success('Opération de facturation traitée avec succès.');
            }
            this.ngOnInit(); // Reload page data
          },
          error: (e) => {
            const serverMsg = e.error?.message || e.error || e.message || 'Erreur inconnue';
            Swal.fire({
              title: 'Erreur',
              text: `L'opération de facturation a échoué.\nDétails: ${serverMsg}`,
              icon: 'error',
              confirmButtonText: 'Fermer'
            });
            console.error(e);
          }
        });
        break;
    }
  }

  private parseDateString(dateString: any): Date | undefined {
    if (!dateString) return undefined;
    if (typeof dateString === 'string') {
       let normalized = dateString;
       if (/(?:[+-]\d{2})$/.test(normalized)) {
          normalized += ':00';
       }
       const d = new Date(normalized);
       return isNaN(d.getTime()) ? undefined : d;
    }
    return dateString;
  }

  datState(o: Sales_order, state: any): Date | string | undefined {
    // Get formatted date for current status
    if (o !== undefined && state !== undefined && o.dates) {
      const stateKey = getStateKey(state);
      if (stateKey && o.dates[stateKey]) {
        return this.parseDateString(o.dates[stateKey]);
      }
    }
    return undefined;
  }

  getLabel(state: any): string {
    return getOrderLabel(state);
  }
}
